import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Box, Typography, Container, Grid, Card } from '@mui/material';
import { Group, RocketLaunch, AddCircleOutline, Settings, Timeline, Visibility, Dashboard, Star } from '@mui/icons-material';
import Navbar from '../Components/LandingComponent/Navbar';
import Footer from '../Components/LandingComponent/Footer';
import { getCookie, setCookie, removeCookie } from '../utils/cookieUtils';

const LandingPage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    // Check cookies for authentication status
    const userData = getCookie('userData');
    return !!userData;
  });
  
  const cardsRef = useRef([]);
  const [typedText, setTypedText] = useState('');
  const [isTyping, setIsTyping] = useState(true);
  const fullText = "Get Started";

  const handleSignIn = () => {
    setIsAuthenticated(true);
    setCookie('isAuthenticated', true);
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    removeCookie('isAuthenticated');
    removeCookie('userData');
  };

  const scrollToSection = (sectionId) => {
    document.getElementById(sectionId).scrollIntoView({ behavior: 'smooth' });
  };

  const handleIntersection = useCallback((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        const index = cardsRef.current.indexOf(entry.target);
        // Handle what you want to do with intersection logic
      }
    });
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(handleIntersection, {
      threshold: 0.1,
    });

    const currentRefs = cardsRef.current;
    if (currentRefs) {
      currentRefs.forEach((card) => {
        observer.observe(card);
      });
    }

    return () => {
      if (currentRefs) {
        currentRefs.forEach((card) => {
          observer.unobserve(card);
        });
      }
    };
  }, [handleIntersection]);

  // Typing Effect Logic
  useEffect(() => {
    let charIndex = 0;
    if (isTyping) {
      const typingInterval = setInterval(() => {
        setTypedText((prev) => fullText.slice(0, charIndex + 1));
        charIndex++;
        if (charIndex === fullText.length) {
          clearInterval(typingInterval);
          setIsTyping(false);
        }
      }, 300); // Adjust typing speed here (100ms per character)

      return () => clearInterval(typingInterval);
    }
  }, [isTyping]);

  return (
    <Box sx={{ backgroundColor: '#f0f4f8', minHeight: '100vh', paddingTop: '64px' }}>
      {/* Navbar */}
      <Navbar
        isAuthenticated={isAuthenticated}
        handleSignIn={handleSignIn}
        handleLogout={handleLogout}
      />

      {/* Hero Section */}
      <Box sx={{ backgroundColor: '#1976d2', color: 'white', py: 10, textAlign: 'center' }}>
        <Typography variant="h2" gutterBottom>
          Welcome to OV Plan
        </Typography>
        <Typography variant="h5" gutterBottom>
          Your ultimate project management tool with boards, cards, projects, and more!
        </Typography>
        <Typography
          variant="h4"
          sx={{
            color: 'white',
            fontWeight: 'bold',
            fontFamily: 'monospace',
            display: 'inline',
            position: 'relative',
            whiteSpace: 'pre'
          }}
        >
          {typedText}
          {isTyping && <span style={{ borderRight: '2px solid white', animation: 'blink 1s step-end infinite', marginLeft: '5px' }} />}
        </Typography>
      </Box>

      {/* Features Introduction Section */}
      <Container sx={{ py: 8 }}>
        <Typography variant="h4" align="center" gutterBottom>
          Understanding the Key Features
        </Typography>
        <Grid container spacing={4} justifyContent="center">
          {/* Boards */}
          <Grid item xs={12} md={6} lg={3}>
            <Card
              sx={{
                p: 3,
                backgroundColor: '#e3f2fd',
                textAlign: 'center',
                boxShadow: 3,
                borderRadius: 2,
                transition: 'transform 0.3s, box-shadow 0.3s',
                '&:hover': {
                  transform: 'scale(1.05)',
                  boxShadow: 6,
                },
              }}
            >
              <Settings sx={{ fontSize: 80, color: '#1976d2' }} />
              <Typography variant="h6" gutterBottom>
                Boards
              </Typography>
              <Typography variant="body1">
                Visualize and manage your projects using boards that represent different stages of your workflow. Each board can have multiple lists to organize tasks efficiently.
              </Typography>
            </Card>
          </Grid>

          {/* Cards */}
          <Grid item xs={12} md={6} lg={3}>
            <Card
              sx={{
                p: 3,
                backgroundColor: '#e8f5e9',
                textAlign: 'center',
                boxShadow: 3,
                borderRadius: 2,
                transition: 'transform 0.3s, box-shadow 0.3s',
                '&:hover': {
                  transform: 'scale(1.05)',
                  boxShadow: 6,
                },
              }}
            >
              <Timeline sx={{ fontSize: 80, color: '#388e3c' }} />
              <Typography variant="h6" gutterBottom>
                Cards
              </Typography>
              <Typography variant="body1">
                Cards are individual items within a board that represent tasks or units of work. They can be customized, moved between lists, and include detailed information such as due dates, attachments, and comments.
              </Typography>
            </Card>
          </Grid>

          {/* Projects */}
          <Grid item xs={12} md={6} lg={3}>
            <Card
              sx={{
                p: 3,
                backgroundColor: '#fffde7',
                textAlign: 'center',
                boxShadow: 3,
                borderRadius: 2,
                transition: 'transform 0.3s, box-shadow 0.3s',
                '&:hover': {
                  transform: 'scale(1.05)',
                  boxShadow: 6,
                },
              }}
            >
              <Group sx={{ fontSize: 80, color: '#ffb300' }} />
              <Typography variant="h6" gutterBottom>
                Projects
              </Typography>
              <Typography variant="body1">
                A project encompasses all the boards and cards related to a specific goal or set of tasks. Projects help in tracking progress, managing resources, and achieving milestones.
              </Typography>
            </Card>
          </Grid>

          {/* Agile Methodology */}
          <Grid item xs={12} md={6} lg={3}>
            <Card
              sx={{
                p: 3,
                backgroundColor: '#fce4ec',
                textAlign: 'center',
                boxShadow: 3,
                borderRadius: 2,
                transition: 'transform 0.3s, box-shadow 0.3s',
                '&:hover': {
                  transform: 'scale(1.05)',
                  boxShadow: 6,
                },
              }}
            >
              <Visibility sx={{ fontSize: 80, color: '#d32f2f' }} />
              <Typography variant="h6" gutterBottom>
                Agile Methodology
              </Typography>
              <Typography variant="body1">
                A project management approach that emphasizes iterative progress, collaboration, and flexibility. Agile methodologies help teams adapt to changes quickly and improve productivity.
              </Typography>
            </Card>
          </Grid>

          {/* Drag-and-Drop Feature */}
          <Grid item xs={12} md={6} lg={3}>
            <Card
              sx={{
                p: 3,
                backgroundColor: '#e3f2fd',
                textAlign: 'center',
                boxShadow: 3,
                borderRadius: 2,
                transition: 'transform 0.3s, box-shadow 0.3s',
                '&:hover': {
                  transform: 'scale(1.05)',
                  boxShadow: 6,
                },
              }}
            >
              <RocketLaunch sx={{ fontSize: 80, color: '#1976d2' }} />
              <Typography variant="h6" gutterBottom>
                Drag-and-Drop
              </Typography>
              <Typography variant="body1">
                Easily manage your tasks and projects with an intuitive drag-and-drop interface. Rearrange tasks, update progress, and prioritize activities with ease.
              </Typography>
            </Card>
          </Grid>
        </Grid>
      </Container>

      {/* Why Choose OV Plan Section */}
      <Container sx={{ py: 8 }} id="features-section">
        <Typography variant="h4" align="center" gutterBottom>
          Why Choose OV Plan?
        </Typography>
        <Grid container spacing={4} justifyContent="center">
          <Grid item xs={12} sm={6} md={4}>
            <Card sx={{ p: 3, backgroundColor: '#e3f2fd', textAlign: 'center', boxShadow: 3, borderRadius: 2 }}>
              <Dashboard sx={{ fontSize: 50, color: '#1976d2' }} />
              <Typography variant="h6" gutterBottom>
                Comprehensive Dashboard
              </Typography>
              <Typography variant="body1">
                Get a complete overview of your projects with our detailed dashboard. Track progress, manage tasks, and analyze performance all in one place.
              </Typography>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Card sx={{ p: 3, backgroundColor: '#e8f5e9', textAlign: 'center', boxShadow: 3, borderRadius: 2 }}>
              <Star sx={{ fontSize: 50, color: '#388e3c' }} />
              <Typography variant="h6" gutterBottom>
                Exceptional User Experience
              </Typography>
              <Typography variant="body1">
                Enjoy an intuitive and user-friendly interface designed to enhance your productivity. Our platform provides a seamless experience tailored to your needs.
              </Typography>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Card sx={{ p: 3, backgroundColor: '#fffde7', textAlign: 'center', boxShadow: 3, borderRadius: 2 }}>
              <AddCircleOutline sx={{ fontSize: 50, color: '#ffb300' }} />
              <Typography variant="h6" gutterBottom>
                Easy Onboarding
              </Typography>
              <Typography variant="body1">
                Start using OV Plan effortlessly with our straightforward onboarding process. Set up your projects and get started in no time.
              </Typography>
            </Card>
          </Grid>
        </Grid>
      </Container>

      {/* Footer */}
      <Footer />
    </Box>
  );
};

export default LandingPage;